import React from 'react'
import { PropTypes } from 'prop-types'
import { useMutation } from '@apollo/client'
import useCurrentUser from 'lib/hooks/useCurrentUser'

import ActionLogic from 'components/Util/ActionLogic'

import updateVehicleStatus from 'graphql/vehicle/updateVehicleStatus.gql'
import getVehicleProfileView from 'graphql/view/getVehicleProfileView.gql'

/**
 * VehicleStatusLogic Component
 *
 * This component provides a mutation for updating the vehicle's status and manages loading and error states.
 * It wraps its child components with `ActionLogic` state management, allowing the child components to trigger the `setVehicleStatus` function to update the vehicle's status via GraphQL.
 */

const VehicleStatusLogic = ({ children }) => {
    const currentUser = useCurrentUser()
    const [updateVehicleStatusMutation] = useMutation(updateVehicleStatus)

    const setVehicleStatus = async (actionLogic, vehicleId, vehicleStatus, statusInfo) => {
        actionLogic.asyncSetState({
            error: false,
            loading: true,
        })

        return updateVehicleStatusMutation({
            variables: {
                vehicleId: vehicleId,
                status: vehicleStatus,
                soldInfo: statusInfo,
            },
            refetchQueries: [{
                query: getVehicleProfileView,
                variables: {
                    vehicleId: vehicleId,
                    userId: currentUser.me.id,
                }
            }],
            awaitRefetchQueries: true,
        }).then((result) => {
            actionLogic.asyncSetState({
                error: false,
                loading: false,
            })

            return Promise.resolve(result)
        }).catch((e) => {
            actionLogic.asyncSetState({
                error: e,
                loading: false,
            })

            return Promise.reject(e)
        })
    }

    return (
        <ActionLogic>
            {(actionLogic) => (
                children({
                    ...actionLogic,
                    setVehicleStatus: (...args) => setVehicleStatus(actionLogic, ...args),
                })
            )}
        </ActionLogic>
    )
}

VehicleStatusLogic.propTypes = {
    children: PropTypes.func.isRequired,
}

export default VehicleStatusLogic
